import { FormEvent, useState } from "react";
import { changePassword } from "../lib/auth";

/** PRD §11.3 -- the same eight-character floor every account gets, minors included. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Change your own password without pretending to have forgotten it.
 *
 * The only password change the app had was the emailed forgot-password code, so an adult who simply
 * wanted to rotate their password had to go through account recovery to do it. This is the ordinary
 * path, and it's shared by the coach and family settings pages because the mechanics are identical --
 * Cognito verifies the current password itself.
 */
export function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Those two passwords don't match.");
      return;
    }
    // Cognito accepts this happily and it is almost always a mistake -- someone who meant to change
    // their password and didn't, then believes they have.
    if (newPassword === currentPassword) {
      setError("That's the password you already have. Choose a different one.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setDone(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      // Cognito's own message is the useful one here ("Incorrect username or password", the password
      // policy it rejected against), so it is shown rather than replaced with something vaguer.
      setError(err instanceof Error ? err.message : "Couldn't change your password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="plan-form" onSubmit={handleSubmit}>
      <label htmlFor="current-password">Current password</label>
      <input
        id="current-password"
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        autoComplete="current-password"
        required
      />

      <label htmlFor="new-password">New password</label>
      <input
        id="new-password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        autoComplete="new-password"
        required
      />

      <label htmlFor="confirm-new-password">Confirm new password</label>
      <input
        id="confirm-new-password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        autoComplete="new-password"
        required
      />

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="badge badge-ok">
          Password changed.
        </p>
      )}

      <button type="submit" disabled={submitting}>
        {submitting ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}
